import { memo, useContext, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { ActionPlusIcon, ActionsIcon, SuccessIcon } from "../../../../assets/icons/icon";
import { PopoverMenu, FilterNonIdeal } from "../../../common";
import AddQuickItem from "../AddQuickItem";
import { Sync } from "../../../../SyncService";
import { LanguageContext } from "../../../../context/LanguageContext";
import { useSnackbar } from "../../../../context/SnackbarContext";
import {
  AppCheckbox,
  AppPagination,
  ParagraphText,
} from "../../../../Libraries";
import { useAuthContext } from "../../../../context/AuthContext";
import ItemRow from "./ItemRow";
import { useNavigate } from "react-router-dom";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { fetchUnArchivedStockCount } from "../../../../database/helpers/stock";
import styles from "./StockItemList.module.css";
import { sentenceCase } from "../../../../constants";

const BASE_COLUMNS = [
  "name",
  "stock",
  "low_stock",
  "unit",
  "cost_price",
  "selling_price",
];

const OPTIONAL_COLUMNS = [
  "barcode",
  "expiry_date",
  "batch_number",
  "wholesale_price",
  "manufacturing_date",
];

const RIGHT_ALIGNED_COLUMNS = new Set([
  "cost_price",
  "selling_price",
  "wholesale_price",
]);

const StockItemList = memo(
  ({
    data,
    setData,
    actions,
    className,
    totalAllStocks,
    totalStockCount,
    resetFilters,
    isLoading,
    getMoreStockData = () => {},
    getInitialStock = () => {},
  }) => {
    const [columns, setColumns] = useState(BASE_COLUMNS);
    const [columnsTemp, setcolumnsTemp] = useState([...BASE_COLUMNS]);

    const { business, user } = useAuthContext();
    const [I18n] = useContext(LanguageContext);
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const selectedOptionalCount = OPTIONAL_COLUMNS.filter((columnId) =>
      columns.includes(columnId),
    ).length;
    const visibleColumns = [
      ...BASE_COLUMNS,
      ...OPTIONAL_COLUMNS.filter((columnId) => columns.includes(columnId)),
    ];

    const NAME_MIN_WIDTH = 200;
    const COLUMN_MIN_WIDTH = 110;
    const DATE_COLUMN_MIN_WIDTH = 130;
    const ACTION_MIN_WIDTH = 120;

    const getColumnMinWidth = (columnId) =>
      columnId === "expiry_date" || columnId === "manufacturing_date"
        ? DATE_COLUMN_MIN_WIDTH
        : COLUMN_MIN_WIDTH;

    const minTableWidth =
      NAME_MIN_WIDTH +
      visibleColumns
        .slice(1)
        .reduce((sum, columnId) => sum + getColumnMinWidth(columnId), 0) +
      (actions ? ACTION_MIN_WIDTH : 0);

    const gridTemplateColumns = [
      `minmax(${NAME_MIN_WIDTH}px, 2fr)`,
      ...visibleColumns
        .slice(1)
        .map((columnId) => `minmax(${getColumnMinWidth(columnId)}px, 1fr)`),
      ...(actions ? [`${ACTION_MIN_WIDTH}px`] : []),
    ].join(" ");

    const shouldEnableHorizontalScroll = selectedOptionalCount > 0;

    const getColumnLabel = (columnId) => {
      switch (columnId) {
        case "name":
          return I18n.item;
        case "stock":
          return I18n.available_stock;
        case "low_stock":
          return I18n.low_stock;
        case "unit":
          return I18n.unit_type;
        case "cost_price":
          return I18n.cost_price;
        case "selling_price":
          return I18n.sell_price;
        case "barcode":
          return I18n.barcode;
        case "expiry_date":
          return I18n.expiry_date;
        case "batch_number":
          return I18n.batch_number;
        case "wholesale_price":
          return I18n.wholesale_price;
        case "manufacturing_date":
          return I18n.manufacturing_date;
        default:
          return columnId;
      }
    };
    const updateColumns = (id) => {
      if (!columnsTemp.includes(id)) {
        setcolumnsTemp((prev) => [...prev, id]);
      } else {
        setcolumnsTemp((prev) => prev.filter((columnId) => columnId !== id));
      }
    };

    const handleUpdateBarcode = () => {
      updateColumns("barcode");
    };

    const handleUpdateExpiryDate = () => {
      updateColumns("expiry_date");
    };

    const handleUpdateBatchnumber = () => {
      updateColumns("batch_number");
    };

    const handleUpdateWholesalePrice = () => {
      updateColumns("wholesale_price");
    };

    const handleUpdateManufacturingDate = () => {
      updateColumns("manufacturing_date");
    };

    const actionOptions = () => [
      {
        label: (
          <Box
            className={styles.popOverCheckboxBox}
            id="add_more_item_upateBarcode"
            onClick={handleUpdateBarcode}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) handleUpdateBarcode();
            }}
          >
            <AppCheckbox
              checked={columnsTemp.includes("barcode")}
              label={I18n.barcode}
            />
          </Box>
        ),
        action: (e) => {},
      },
      {
        label: (
          <Box
            className={styles.popOverCheckboxBox}
            id="add_more_item_expiryDate"
            onClick={handleUpdateExpiryDate}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) handleUpdateExpiryDate();
            }}
          >
            <AppCheckbox
              checked={columnsTemp.includes("expiry_date")}
              label={I18n.expiry_date}
            />
          </Box>
        ),
        action: () => {},
      },
      {
        label: (
          <Box
            className={styles.popOverCheckboxBox}
            id="add_more_item_batchnumber"
            onClick={handleUpdateBatchnumber}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) handleUpdateBatchnumber();
            }}
          >
            <AppCheckbox
              checked={columnsTemp.includes("batch_number")}
              label={I18n.batch_number}
            />
          </Box>
        ),
        action: () => {},
      },
      {
        label: (
          <Box
            id="add_more_item_wholesalePrice"
            className={styles.popOverCheckboxBox}
            onClick={handleUpdateWholesalePrice}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey)
                handleUpdateWholesalePrice();
            }}
          >
            <AppCheckbox
              checked={columnsTemp.includes("wholesale_price")}
              label={I18n.wholesale_price}
            />
          </Box>
        ),
        action: () => {},
      },
      {
        label: (
          <Box
            id="add_more_item_manufacturingDate"
            className={styles.popOverCheckboxBox}
            onClick={handleUpdateManufacturingDate}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey)
                handleUpdateManufacturingDate();
            }}
          >
            <AppCheckbox
              checked={columnsTemp.includes("manufacturing_date")}
              label={I18n.manufacturing_date}
            />
          </Box>
        ),
        action: () => {},
      },
    ];

    const handleSave = async (new_item) => {
      let paramsForStock = {
        name: new_item?.name,
        stock: Number(new_item?.stock || 0),
        cost_price: Number(new_item?.cost_price || 0),
        selling_price: Number(new_item?.selling_price || 0),
        unit: new_item?.unit || "Pieces",
        secondary_unit: new_item?.secondary_unit,
        unit_conversion: new_item?.unit_conversion
          ? Number(new_item?.unit_conversion)
          : null,
        imei: null,
        batch_number: new_item?.batch_number
          ? Number(new_item?.batch_number)
          : null,
        barcode: new_item?.barcode ? Number(new_item?.barcode) : null,
        wholesale_price: new_item?.wholesale_price
          ? Number(new_item?.wholesale_price)
          : null,
        expiry_date: new_item?.expiry_date,
        manufacturing_date: new_item?.manufacturing_date,
        low_stock: new_item?.low_stock ? Number(new_item?.low_stock) : null,
        image_url: new_item?.image_url ? new_item?.image_url : null,
        expiry_alert: new_item?.expiry_alert
          ? Number(new_item?.expiry_alert)
          : null,
        minimum_order_quantity: new_item?.minimum_order_quantity
          ? Number(new_item?.minimum_order_quantity)
          : 0,
      };

      let created_stock = await business.createStock(paramsForStock);

      if (paramsForStock.stock) {
        let paramsForInv = {
          unit_conversion: created_stock.unit_conversion,
          transacted_at: created_stock.created_at,
          price: paramsForStock.cost_price,
          quantity: paramsForStock.stock,
          stock: paramsForStock.stock,
          name: paramsForStock.name,
          is_secondary_unit: false,
          type: "in",
        };

        await created_stock.createInventory(paramsForInv);
      }
      const stockByBussiness = await fetchUnArchivedStockCount();
      if (stockByBussiness === 5) {
        triggerLogEvent(analytics, "sb_PU");
      }
      getInitialStock();
      Sync(user.phone_number, navigate);
      showSnackbar({
        message: "Quick item created in the stock",
        icon: <SuccessIcon />,
        severity: "success",
      });
      triggerLogEvent(analytics, "sb_quick_item_created", {
        name: paramsForStock.name,
        stock: paramsForStock.stock,
        cost_price: paramsForStock.cost_price,
        selling_price: paramsForStock.selling_price,
      });
    };
    const handleViewItemReport = (dataItem) => {
      navigate(`/Inventory/ItemReport/${dataItem.id}`);
    };

    const handleAddEvent = (columnsTemp) => {
      if (!columns.includes("barcode") && columnsTemp.includes("barcode")) {
        triggerLogEvent(analytics, "sb_add_barcode", {});
      }
      if (
        !columns.includes("expiry_date") &&
        columnsTemp.includes("expiry_date")
      ) {
        triggerLogEvent(analytics, "sb_add_expiry_date", {});
      }
      if (
        !columns.includes("batch_number") &&
        columnsTemp.includes("batch_number")
      ) {
        triggerLogEvent(analytics, "sb_add_batch", {});
      }
      if (
        !columns.includes("wholesale_price") &&
        columnsTemp.includes("wholesale_price")
      ) {
        triggerLogEvent(analytics, "sb_add_ws_price", {});
      }
      if (
        !columns.includes("manufacturing_date") &&
        columnsTemp.includes("manufacturing_date")
      ) {
        triggerLogEvent(analytics, "sb_add_manufac_date", {});
      }
      if (!columns.includes("low_stock") && columnsTemp.includes("low_stock")) {
        triggerLogEvent(analytics, "sb_add_low_stock", {});
      }
    };
    return (
      <Box
        className={`${styles.itemsContainer} ${className}`}
        sx={{
          height: "calc(100% - 138px)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Box
          id="scrollableDiv"
          className={`generic-scrollbar ${styles.tableScrollArea}`}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            width: "100%",
          }}
        >
          <Box
            className={styles.tableScrollInner}
            sx={{
              width: "100%",
              minWidth: shouldEnableHorizontalScroll ? minTableWidth : 0,
            }}
          >
            <Box
              className={styles.headerColumnsContainer}
              sx={{
                width: "100%",
                boxSizing: "border-box",
                gridTemplateColumns,
                [`& .${styles.headerColumns}`]: {
                  minWidth: 0,
                  boxSizing: "border-box",
                },
                [`& .${styles.fixedActionsColumnHeader}`]: {
                  minWidth: 0,
                  boxSizing: "border-box",
                },
              }}
            >
              {visibleColumns.map((columnId) => (
                <Box
                  key={columnId}
                  className={`${styles.headerColumns} ${
                    columnId === "name" ? styles.stockItemNameColumn : ""
                  } ${RIGHT_ALIGNED_COLUMNS.has(columnId) ? styles.AlignRight : ""}`}
                >
                  <ParagraphText className={styles.headerTypo}>
                    {getColumnLabel(columnId)}
                  </ParagraphText>
                </Box>
              ))}

              {actions && (
                <Box className={styles.fixedActionsColumnHeader}>
                  <ParagraphText className={`${styles.headerTypo} ${styles.actionsColumnHeaderTypo}`}>
                    {I18n.actions}
                  </ParagraphText>
                  <PopoverMenu
                    UbButtonClass="StockPopoverMenu"
                    className={styles.stockPopoverMenu}
                    popoverPaperClassName={styles.stockPopoverPaper}
                    openPopoverClassName={styles.actionsPopoverOpen}
                    popoverButtonClassName={styles.actionsPlusBtn}
                    popOverStyle={{ width: "249px", fontSize: "10px" }}
                    menuOptions={actionOptions()}
                    closeOnSelect={false}
                    id="stock_item_list_menu"
                    handleOpen={() =>
                      triggerLogEvent(analytics, "sb_add_options")
                    }
                    popoverButton={
                      <Tooltip
                        placement="bottom-end"
                        disableFocusListener
                        title={
                          <Box>
                            <ParagraphText className={"fontSize14 fontWeight500"}>
                              {sentenceCase(I18n.add_more_item_detail)}
                            </ParagraphText>
                            <Box marginBottom={1} />
                            <ParagraphText>
                              {I18n.add_more_item_detail_desc}
                            </ParagraphText>
                          </Box>
                        }
                        arrow
                        slotProps={{
                          popper: {
                            sx: { pointerEvents: "none" },
                          },
                          tooltip: {
                            sx: {
                              backgroundColor: "#24272F",
                              padding: "0.5rem",
                              color: "#fff",
                              fontFamily: "outfit",
                            },
                          },
                          arrow: {
                            sx: {
                              color: "#24272F",
                              fontSize: "1rem",
                            },
                          },
                        }}
                      >
                        <Box
                          component="span"
                          className={styles.actionsPlusTooltipTarget}
                        >
                          <ActionPlusIcon color="#0050AB" />
                        </Box>
                      </Tooltip>
                    }
                    actionBtn={() => {
                      handleAddEvent([...columnsTemp]);
                      setColumns([...columnsTemp]);
                    }}
                  />
                </Box>
              )}
            </Box>

            {!isLoading && totalAllStocks.length !== 0 && data.length !== 0 && (
              <AddQuickItem
                visibleColumns={visibleColumns}
                data={data}
                setData={setData}
                handleSave={handleSave}
                business={business}
                gridTemplateColumns={gridTemplateColumns}
              />
            )}

            {data.length > 0 ? (
            <AppPagination
              data={{
                handleNext: (e) => {
                  if (totalStockCount > data.length) {
                    getMoreStockData();
                  }
                },
                pageLoad: isLoading,
                count: totalStockCount,
                list: data,
                scrollableTarget: "scrollableDiv",
              }}
            >
              <Box className={styles.dataRowsContainer} sx={{ width: "100%" }}>
                {data.map((dataItem, rowIndex) => (
                  <ItemRow
                    visibleColumns={visibleColumns}
                    key={rowIndex}
                    index={rowIndex}
                    dataItem={dataItem}
                    rowIndex={rowIndex}
                    data={data}
                    className={className}
                    actions={actions}
                    handleItemReport={handleViewItemReport}
                    getInitialStock={getInitialStock}
                    gridTemplateColumns={gridTemplateColumns}
                  />
                ))}
              </Box>
            </AppPagination>
          ) : (
            !isLoading && (
              <Box className={`h-100 ${styles.emptyStateWrapper}`}>
                <FilterNonIdeal
                  title={I18n.no_transactions_matching_those_filters}
                  subTitle={I18n.try_adjusting_filters}
                  subTitleTextOnClick={() => resetFilters()}
                  subTitleText={I18n.reset_small}
                />
              </Box>
            )
          )}
          </Box>
        </Box>
      </Box>
    );
  },
);

export default StockItemList;
